/**
 * Rock Off Retail — countdown announcement bar.
 * Progressive enhancement: with JS off, the bar just shows the static
 * message + CTA (no chip, since the chip markup is only injected when an
 * end_datetime is set — see sections/rockoff-countdown.liquid).
 *
 * 20 Oct 2026: the bar also rotates through its messages (the section's own
 * message first, then any Message blocks). One message is shown at a time;
 * the others are visibility:hidden, so they cannot be focused. Rotation
 * pauses on hover and keyboard focus, stops for good on a touch, waits while
 * the tab is hidden, never runs for prefers-reduced-motion, and settles on
 * the first message after three full cycles (so it is not moving forever).
 * Each block's End is re-checked here in UK time on load (Shopify can serve
 * a page rendered before it ended), every minute and when the tab returns.
 * With the pre-20-Oct markup (no slides) it behaves exactly as before, so a
 * deploy that stops between this file and the section file breaks nothing.
 */
(() => {
  if (customElements.get('rockoff-countdown')) return;

  // "YYYY-MM-DD HH:MM" in UK time, the shape snippets/rockoff-schedule-state compares. null if Intl is missing.
  const ukNow = () => {
    try {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
      }).formatToParts(new Date());
      const p = Object.fromEntries(parts.map((x) => [x.type, x.value]));
      return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}`;
    } catch (e) {
      return null;
    }
  };

  class RockoffCountdown extends HTMLElement {
    connectedCallback() {
      this.designMode = this.hasAttribute('data-design-mode');
      this.mainSlide = this.querySelector('[data-countdown-main]');
      this.legacy = !this.querySelector('[data-countdown-slide]'); // pre-20-Oct markup
      if (!this.legacy) {
        this.dropEnded();
        this.setupRotation();
        if (!this.designMode) this.recheck = setInterval(() => this.dropEnded(), 60000);
      }
      this.setupCountdown();
    }

    disconnectedCallback() {
      if (this.interval) clearInterval(this.interval);
      clearInterval(this.recheck);
      clearTimeout(this.rotateTimer);
      if (this.unbind) this.unbind();
    }

    // ---- countdown (unchanged behaviour, now scoped to the first message) ----

    setupCountdown() {
      const endRaw = this.dataset.end;
      this.hideOnExpiry = this.dataset.expiredHide === 'true';
      this.chip = this.querySelector('[data-countdown-chip]');
      this.mainRow = this.querySelector('[data-countdown-row]');
      this.fallbackRow = this.querySelector('[data-countdown-fallback]');

      if (!endRaw || !this.chip) return; // static bar, nothing to tick

      // Treat the stored value as UTC if no timezone offset is present.
      const iso = /[zZ]|[+-]\d\d:\d\d$/.test(endRaw) ? endRaw : `${endRaw}:00Z`;
      this.endTime = new Date(iso).getTime();

      if (Number.isNaN(this.endTime)) return;

      this.tick();
      this.interval = setInterval(() => this.tick(), 1000);
    }

    tick() {
      const remainingMs = this.endTime - Date.now();

      if (remainingMs <= 0) {
        clearInterval(this.interval);
        this.expire();
        return;
      }

      const totalSeconds = Math.floor(remainingMs / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      const pad = (n) => String(n).padStart(2, '0');

      this.chip.textContent = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    }

    expire() {
      if (this.legacy) {
        if (!this.hideOnExpiry && this.fallbackRow) {
          this.mainRow.setAttribute('hidden', 'hidden');
          this.fallbackRow.removeAttribute('hidden');
        } else {
          this.setAttribute('hidden', 'hidden');
        }
        return;
      }
      if (!this.hideOnExpiry && this.fallbackRow) {
        this.mainRow.setAttribute('hidden', 'hidden');
        this.fallbackRow.removeAttribute('hidden');
        return;
      }
      // Hide the countdown message. The bar itself only hides when no other message is left.
      this.removeSlide(this.mainSlide);
    }

    // ---- rotation ----

    dropEnded() {
      if (this.designMode) return;
      const now = ukNow();
      if (!now) return;
      this.querySelectorAll('[data-countdown-slide]').forEach((s) => {
        if ((s.dataset.end && s.dataset.end <= now) || (s.dataset.start && s.dataset.start > now)) this.removeSlide(s);
      });
    }

    slidesNow() {
      return [...this.querySelectorAll('[data-countdown-slide]')];
    }

    removeSlide(slide) {
      if (!slide || !slide.isConnected) return;
      const wasActive = slide.classList.contains('is-active');
      slide.remove();
      const left = this.slidesNow();
      if (!left.length) {
        this.setAttribute('hidden', 'hidden');
        clearTimeout(this.rotateTimer);
        return;
      }
      if (wasActive || !left.some((s) => s.classList.contains('is-active'))) this.show(0);
      this.armRotation();
    }

    setupRotation() {
      const slides = this.slidesNow();
      if (!slides.length) {
        this.setAttribute('hidden', 'hidden');
        return;
      }
      if (!slides.some((s) => s.classList.contains('is-active'))) this.show(0);

      this.shown = 0;
      this.stopped = false;
      this.delay = Math.max(3, Number(this.dataset.rotate) || 5) * 1000;
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.hovered = false;
      this.focused = false;
      this.editorHold = false;

      const on = (el, type, fn, opts) => {
        el.addEventListener(type, fn, opts);
        return () => el.removeEventListener(type, fn, opts);
      };
      const offs = [
        on(this, 'pointerenter', (e) => { if (e.pointerType === 'mouse') { this.hovered = true; this.armRotation(); } }),
        on(this, 'pointerleave', (e) => { if (e.pointerType === 'mouse') { this.hovered = false; this.armRotation(); } }),
        on(this, 'touchstart', () => { this.stopped = true; this.armRotation(); }, { passive: true }),
        on(this, 'focusin', () => { this.focused = true; this.armRotation(); }),
        on(this, 'focusout', (e) => { if (!this.contains(e.relatedTarget)) { this.focused = false; this.armRotation(); } }),
        on(document, 'visibilitychange', () => { if (!document.hidden) this.dropEnded(); this.armRotation(); }),
        on(this.reduced, 'change', () => this.armRotation()),
      ];
      if (this.designMode) {
        offs.push(on(document, 'shopify:block:select', (e) => {
          const i = this.slidesNow().indexOf(e.target);
          if (i < 0) return;
          this.editorHold = true;
          this.show(i);
          this.armRotation();
        }));
        offs.push(on(document, 'shopify:block:deselect', (e) => {
          if (this.slidesNow().indexOf(e.target) < 0) return;
          this.editorHold = false;
          this.armRotation();
        }));
      }
      this.unbind = () => offs.forEach((off) => off());
      this.armRotation();
    }

    armRotation() {
      clearTimeout(this.rotateTimer);
      const slides = this.slidesNow();
      if (!this.delay || slides.length < 2 || this.stopped || this.reduced.matches || this.hovered || this.focused || this.editorHold || document.hidden) return;
      this.rotateTimer = setTimeout(() => {
        const list = this.slidesNow();
        const cur = list.findIndex((s) => s.classList.contains('is-active'));
        this.shown += 1;
        if (!this.designMode && this.shown >= list.length * 3) {
          this.show(0); // three full cycles: rest on the first message
          this.stopped = true;
          return;
        }
        this.show((cur + 1) % list.length);
        this.armRotation();
      }, this.delay);
    }

    show(i) {
      this.slidesNow().forEach((s, n) => {
        s.classList.toggle('is-active', n === i); // the others are visibility:hidden (CSS), so out of the accessibility tree
      });
    }
  }

  customElements.define('rockoff-countdown', RockoffCountdown);
})();
